import { useMemo } from 'react';
import { Link, useParams } from 'react-router';
import { marked } from 'marked';
import { useQuery } from '@tanstack/react-query';
import { can, type TeamSummary } from '@hoopify/shared';
import { api } from '../lib/api';
import { ARTICLES, PLAYER_ARTICLES } from '../help';
import { LoadingBlock, PageHeader } from '../components/ui';
import { NotFoundPage } from './NotFoundPage';

export function HelpPage() {
  const { slug } = useParams();
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api.get<TeamSummary[]>('/teams') });
  // Players and parents get the short list; coaches, read-only members and anyone with no team yet see it all.
  const coach = !teams.data?.length || teams.data.some((t) => can(t.role, 'sessions:details'));
  const list = coach ? ARTICLES : ARTICLES.filter((a) => PLAYER_ARTICLES.includes(a.slug)).sort((a, b) => PLAYER_ARTICLES.indexOf(a.slug) - PLAYER_ARTICLES.indexOf(b.slug));
  const article = slug ? list.find((a) => a.slug === slug) : list[0];
  // Articles are our own files shipped with the app, not user content.
  const html = useMemo(() => (article ? (marked.parse(article.body, { async: false }) as string) : ''), [article]);
  if (teams.isLoading) return <LoadingBlock />;
  if (!article) return <NotFoundPage />;

  return (
    <>
      <PageHeader title="Help" />
      <div className="grid gap-8 md:grid-cols-[200px_1fr]">
        <nav aria-label="Help articles" className="md:sticky md:top-8 md:self-start">
          <ul className="flex gap-1 overflow-x-auto pb-1 md:flex-col md:overflow-visible md:pb-0">
            {list.map((a) => (
              <li key={a.slug} className="shrink-0">
                <Link
                  to={`/help/${a.slug}`}
                  className={`block rounded-md px-2.5 py-1.5 text-[14px] ${a.slug === article.slug ? 'bg-action-soft font-medium text-action' : 'text-muted hover:bg-sunken hover:text-ink'}`}
                  aria-current={a.slug === article.slug ? 'page' : undefined}
                >
                  {a.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <article className="prose-help min-w-0 max-w-[68ch]">
          <h2 className="text-[20px] font-semibold">{article.title}</h2>
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </article>
      </div>
    </>
  );
}
